import { imageToolCall } from "../../types/message";
import "../../Styles/Message/ImageGeneration.css";
import { useAttachmentContext } from "../../contexts/AttachmentContext";
import { Alert02Icon, Download01Icon } from "hugeicons-react";
import { ImageDotField } from "./ImageDotField";
import { useCallback, useState } from "react";

// `continuing` skips the intro when the field picks up where the placeholder
// left off, so the hand-off to the image doesn't flicker.
const ImageLoadingCard = ({ seedKey, continuing = false }: { seedKey: string; continuing?: boolean }) => (
    <div
        className={`image-generation-loading${continuing ? " is-continuing" : ""}`}
        role="status"
        aria-label="Generating Image"
    >
        <ImageDotField seedKey={seedKey} skipIntro={continuing} />
    </div>
);

// Keeps the dot field under the image until it decodes, then sharpens the
// image in over it. Only images that were generated while on screen reveal;
// ones opened from history appear immediately.
const GeneratedImage = ({ url, alt, seedKey, reveal, onClick }: {
    url: string;
    alt: string;
    seedKey: string;
    reveal: boolean;
    onClick: () => void;
}) => {
    const [status, setStatus] = useState<"pending" | "revealing" | "ready">(reveal ? "pending" : "ready");

    const imageRef = useCallback((image: HTMLImageElement | null) => {
        if (image?.complete && image.naturalWidth > 0) {
            setStatus(current => current === "pending" ? "revealing" : current);
        }
    }, []);

    return (
        <>
            {status !== "ready" && <ImageLoadingCard seedKey={seedKey} continuing />}
            <img
                ref={imageRef}
                src={url}
                alt={alt}
                className={`image-generation-image is-${status}`}
                onClick={onClick}
                onLoad={() => setStatus(current => current === "pending" ? "revealing" : current)}
                onError={() => setStatus("ready")}
                onAnimationEnd={() => setStatus("ready")}
            />
        </>
    );
};

interface ImageGenerationProps {
    imageGeneration: imageToolCall;
    isMessageGenerating: boolean;
}

export const ImageGeneration = ({ imageGeneration, isMessageGenerating }: ImageGenerationProps) => {
    const { setImageModalState } = useAttachmentContext();
    const isGenerating = imageGeneration.isLoading && isMessageGenerating;
    const [sawGeneration, setSawGeneration] = useState(isGenerating);
    if (isGenerating && !sawGeneration) setSawGeneration(true);

    const handleImageClick = (url: string) => {
        setImageModalState({
            isOpen: true,
            url: url,
            title: ""
        });
    }

    const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>, url: string) => {
        e.stopPropagation();
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            const filename = url.substring(url.lastIndexOf('/') + 1) || 'generated-image.png';
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading image:', error);
        }
    };

    return (
        <div className="image-generation">
            {isGenerating
                ? Array.from({ length: imageGeneration.numberOfImages || 1 }).map((_, index) => (
                    <div key={`placeholder-${index}`} className="image-container">
                        <ImageLoadingCard seedKey={`${imageGeneration.imageGenerationId}-${index}`} />
                    </div>
                  ))
                : imageGeneration.urls && imageGeneration.urls.length > 0
                  ? imageGeneration.urls.map((url, index) => (
                        <div key={`image-${index}`} className="image-container">
                            <GeneratedImage
                                url={url}
                                alt={`Generated ${index + 1}`}
                                seedKey={`${imageGeneration.imageGenerationId}-${index}`}
                                reveal={sawGeneration}
                                onClick={() => handleImageClick(url)}
                            />
                            <button
                                className="download-button"
                                onClick={(e) => handleDownload(e, url)}
                                title="Download Image"
                                aria-label="Download Image"
                            >
                                <Download01Icon />
                            </button>
                        </div>
                    ))
                  : (
                        <div className="image-generation-error">
                             <Alert02Icon className="w-6 h-6 text-red-500" />
                             <p className="text-sm text-text-secondary">Failed to generate images. Please try again.</p>
                         </div>
                    )
            }
        </div>
    )
}